import {DateTime} from 'luxon';
import {AdSize} from '../adSize/adSize.types';
import {CampaignTrackerTypeName} from '../campaignTrackers/campaignTrackers.types';
import { DV360CreativeStatus } from './campaignPlacements.enums';

interface TrkUrl {
  label: string;
  type: number;
  typeName: string;
  url: string;
}

enum CampaignEnvironment {
  Web = 'web',
  InApp = 'inapp',
  WebOrInApp = 'webOrInApp',
  ThirdPartyTracking = 'thirdPartyTracking',
  CTV = 'ctv',
  DOOH = 'dooh',
}

enum CampaignPlacementEnvironmentValue {
  Web = 0,
  InApp = 10,
  WebOrInApp = 15,
  ThirdPartyTracking = 30,
  CTV = 40,
  DOOH = 50,
}

enum CampaignPlacementEventType {
  None = 'None',
  Impression = 'Impression',
  Click = 'Click',
  LandingPage = 'LandingPage',
  UtmParameters = 'UtmParameters',
  ClickTracking = 'ClickTracking',
  EngageTracking = 'EngageTracking',
  VideoCompleteTracking = 'VideoCompleteTracking',
  ScriptTracker = 'ScriptTracker',
}

export interface DSP_Difference_UI {
  expected: string;
  actual: string;
  fieldKey: string;
}

export interface DSPCreative_UI {
  id: number;
  name: string;
  type: string;
  landingPage?: string;
  isDv360: boolean;
}

export interface RemoteTTDCreative_UI extends DSPCreative_UI {
  audienceId: string;
  flightStartDate: string;
  flightEndDate: string;
  externalPlacementId: string;
}

export interface DV360Creative_UI extends DSPCreative_UI {
  status: DV360CreativeStatus;
  externalAdId?: number;
  externalCreativeId?: number;
  externalPlacementId?: number;
  skippable?: boolean;
  skipOffset?: number;
  pingForAttribution?: boolean;
}

export interface TTDCreative_UI
  extends Omit<RemoteTTDCreative_UI, 'flightStartDate' | 'flightEndDate'> {
  flightStartDate: DateTime;
  flightEndDate: DateTime;
}

export interface DSPAdGroup_UI {
  id: number;
  name: string;
  type: string;
  status: string;
}

export interface RemotePlacementDSPCreative_UI {
  exists: boolean;
  hasIssue: boolean;
  issueMessage: string;
  dspCreative: DV360Creative_UI | RemoteTTDCreative_UI;
  differences: DSP_Difference_UI[];
  width?: number;
  height?: number;
  lastUpdateTimestamp?: string;
}

export interface PlacementDSPCreative_UI
  extends Omit<RemotePlacementDSPCreative_UI, 'dspCreative' | 'lastUpdateTimestamp'> {
  dspCreative: DV360Creative_UI | TTDCreative_UI;
  lastUpdateTimestamp?: DateTime;
}

export interface CustomParams<T extends string | DateTime | boolean> {
  type: number;
  value: T;
}

export interface RemotePlacement_UI {
  id: number;
  dspId: number;
  placementId: string;
  name: string;
  environment: number;
  adSize: AdSize;
  customTextParams: CustomParams<string>[];
  customDateTimeParams: CustomParams<string>[];
  customBooleanParams: CustomParams<boolean>[];
  placementDspCreatives?: RemotePlacementDSPCreative_UI[];
  campaignIntegrationId?: number;
  trackers: {url: string; type: number}[];
  cpm: number;
  dspAdGroup: DSPAdGroup_UI;
  landingPage?: string;
  landingPageParameters?: string;
  targetPlatformId?: number;
}

export interface Placement_UI
  extends Omit<RemotePlacement_UI, 'customDateTimeParams' | 'placementDspCreatives'> {
  customDateTimeParams: CustomParams<DateTime>[];
  customBooleanParams: CustomParams<boolean>[];
  trackers: TrkUrl[];
  placementDspCreatives: PlacementDSPCreative_UI[];
  trackersCount?: Record<CampaignTrackerTypeName, number>;
  environmentName: string;
  dspName: string;
  dspUIName: string;
}

export interface ActivePlacementsSyncJob {
  jobId: number;
  placementIds: number[];
}

export {TrkUrl, CampaignEnvironment, CampaignPlacementEventType, CampaignPlacementEnvironmentValue};
